import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [plan, setPlan] = useState(null);
  const [usage, setUsage] = useState(null);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [gmailEmail, setGmailEmail] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setProfile(data.profile);
      setPlan(data.plan);
      setUsage(data.usage);
      setGmailConnected(data.gmail_connected);
      setGmailEmail(data.gmail_email);
    } catch {
      localStorage.removeItem("token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", data.token);
    await refresh();
    return data;
  };

  const signup = async (email, password, name) => {
    const { data } = await api.post("/auth/signup", { email, password, name });
    localStorage.setItem("token", data.token);
    await refresh();
    return data;
  };

  const loginWithGoogleSession = async (session_id) => {
    // Deprecated: Emergent-managed flow. Kept as no-op for compatibility.
    console.warn("loginWithGoogleSession is deprecated — using direct Google OAuth instead");
    return null;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setProfile(null);
    setPlan(null);
    setUsage(null);
    setGmailConnected(false);
    setGmailEmail(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user, profile, plan, usage, gmailConnected, gmailEmail,
        loading, refresh, login, signup, loginWithGoogleSession, logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
